import { Fragment, type ReactNode } from "react";
import { DOCK_LABELS, THERAGENIE_LABEL } from "../../shared/constants/pageAgentLabels";
import type { AgentTurn } from "../../shared/model/agentTurn";
import { AgentMarkdown } from "./AgentMarkdown";

interface AgentTranscriptProps {
  turns: AgentTurn[];
  /** True while an answer is arriving; drives `aria-live` and `aria-busy` on the turn in flight. */
  streaming: boolean;
  /**
   * What happened for the latest question (steps, working indicator), drawn right after that
   * question and before its answer, so the transcript reads in the order things happened.
   */
  activity?: ReactNode;
  /** What the latest answer produced (the proposal card), drawn after the answer. */
  outcome?: ReactNode;
}

/** The conversation. `aria-live` is scoped to the turn in flight, not the list. */
export function AgentTranscript({ turns, streaming, activity, outcome }: AgentTranscriptProps) {
  if (turns.length === 0) {
    return (
      <div className="pa-empty">
        <p className="pa-empty-title">{DOCK_LABELS.emptyTitle}</p>
        <p className="pa-empty-description">{DOCK_LABELS.emptyDescription}</p>
      </div>
    );
  }
  // The latest question's activity goes right after it; earlier questions keep only their words.
  const lastQuestion = turns.map((turn) => turn.role).lastIndexOf("user");
  return (
    <ol className="pa-transcript">
      {turns.map((turn, index) => {
        const inFlight = streaming && index === turns.length - 1 && turn.role === "assistant";
        return (
          <Fragment key={turn.id}>
            <li className="pa-turn" data-role={turn.role}
              aria-live={inFlight ? "polite" : undefined} aria-busy={inFlight || undefined}>
              <p className="pa-turn-role">{turn.role === "user" ? DOCK_LABELS.you : THERAGENIE_LABEL}</p>
              <AnswerBody turn={turn} inFlight={inFlight} />
              {turn.notice !== null && <p className="pa-turn-notice" role="status">{turn.notice}</p>}
            </li>
            {index === lastQuestion && activity !== undefined && activity !== null && activity !== false && (
              <li className="pa-activity" aria-label={DOCK_LABELS.activityLabel}>{activity}</li>
            )}
          </Fragment>
        );
      })}
      {outcome !== undefined && outcome !== null && outcome !== false && <li className="pa-outcome">{outcome}</li>}
    </ol>
  );
}

function AnswerBody({ turn, inFlight }: { turn: AgentTurn; inFlight: boolean }) {
  if (turn.text !== "") return turn.role === "assistant" ? <AgentMarkdown text={turn.text} /> : <p className="pa-turn-text">{turn.text}</p>;
  // The working indicator above already says what is happening; an empty answer adds nothing.
  if (inFlight) return null;
  if (turn.notice !== null) return null;
  return <p className="pa-turn-text pa-muted">{DOCK_LABELS.noText}</p>;
}
